import { Container, ContainerGutter, ContainerPadding, CTA, Divider, Fieldset, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Lock } from '@volkswagen-onehub/icons-core';
import { VehicleObject } from '../../models/Vehicle';
import { DateTimeFormat } from '../../utils/DateTimeFormat';
import DataField from '../DataField/DataField';
import './EfficiencyDetails.scss';
import RecallButton from '../../molecules/RecallButton/RecallButton';
import { TechnicalDataObject } from '../../models/TechnicalData';
import { DataRequestState } from '../../models/DataRequestState';

export default function EfficiencyDetails(props: Props) {
	const {
		vehicle,
		technicalData,
		isUserAuthorized,
		recallRequestStatus,
		getConsumption,
		getEmissionOrRange,
		numberRecalls,
		toggleRecallsLayer,
		toggleTechnicalDetailsLayer,
		trackingService,
		isLoggedIn,
		onClickDisclaimer,
		footnotes,
	} = props;
	const { t } = useTranslation('ns1', { useSuspense: false });

	const [fuelConsumption, setFuelConsumption] = useState('');
	const [emission, setEmission] = useState('');

	const vinCheckTrackingButton = () => {
		if (trackingService) {
			trackingService.track('VWBasic_Button_Click', {
				eventInfo: {
					eventAction: 'VWBasic_Button_Click',
					pageId: window.location.href,
					pageName: 'Vin Details',
					contentId: 'vinDetails.efficiencyDetails',
					sectionName: 'efficiencyDetails',
					brand: 'passenger',
				},
				user: {
					loginStatus: isLoggedIn,
					browserResolutionHeight: window.innerHeight,
					browserResolutionWidth: window.innerWidth,
					browserResolutionOrientation: window.innerHeight >= window.innerWidth ? 'v' : 'h',
				},
				personalization: {
					personalizable: false,
				},
				configuration: {
					CarlineName: vehicle.data.modelCarLine,
					SalesgroupName: vehicle.data.modelSalesGroup,
				},
				link: {
					name: 'toggleTechnicalDetailsLayer',
				},
			});
		}
	};

	const lockedContent = () => {
		return (
			<div className="height-thirty-two">
				<Lock aria-label={t('general.locked')} />
			</div>
		);
	};

	const getManufacturersWarranty = (): string => {
		let manufacturersWarranty;
		if (vehicle.data.vehicleDetails?.registrationDate && vehicle.data.vehicleDetails.manufacturersWarranty) {
			const warrantyLength = `${vehicle.data.vehicleDetails.manufacturersWarrantyLength || 0} ${i18next.t('efficiencyDetails.years')}`;

			manufacturersWarranty = `${warrantyLength} (${i18next.t('efficiencyDetails.to')} ${DateTimeFormat.germanDateFormat(
				vehicle.data.vehicleDetails.manufacturersWarranty,
			)})`;
		} else {
			manufacturersWarranty = '';
		}
		return manufacturersWarranty;
	};

	const warranty = getManufacturersWarranty();
	let additionalWarranty: boolean = true;
	if (warranty === 'no warranty') {
		additionalWarranty = false;
	}

	useEffect(() => {
		if (technicalData.state === DataRequestState.Available) {
			setFuelConsumption(getConsumption);
			setEmission(getEmissionOrRange);
		}
	}, [getEmissionOrRange, getConsumption, technicalData.state]);

	const getText = (dummy: 'short' | 'medium' | 'long', text: string) => {
		return vehicle.state !== DataRequestState.Available && !isUserAuthorized ? t(`dummyText.${dummy}`) : t(text);
	};

	return (
		<>
			<Container wrap="always" gutter={ContainerGutter.dynamic0050}>
				<>
					{!isUserAuthorized ? (
						<DataField title={`${t('efficiencyDetails.manufacturerWarranty')}`} property="" jsxContent={lockedContent()} />
					) : (
						<DataField
							title={`${t('efficiencyDetails.manufacturerWarranty')}`}
							property={getText('medium', additionalWarranty ? warranty : t('efficiencyDetails.warrantyExpired'))}
							jsxContent={undefined}
						/>
					)}

					<Divider />
				</>

				<>
					{!isUserAuthorized ? (
						<DataField title={`${t('efficiencyDetails.followUpGuarantee')}`} property="" jsxContent={lockedContent()} />
					) : (
						<DataField
							title={`${t('efficiencyDetails.followUpGuarantee')}`}
							property={getText('medium', vehicle.data.vehicleDetails?.subsequentWarranty || '')}
							jsxContent={undefined}
						/>
					)}

					<Divider />
				</>

				{/* TODO 09112021 look if there is a better way to do this */}

				<div className="remove-span-child">
					<Fieldset
						legend={`${t(
							// TODO 15082022 do in backend
							technicalData.data.engineGearboxElectrics?.fuelType === 'Elektro' ||
								technicalData.data.engineGearboxElectrics?.fuelType === 'Electric' ||
								technicalData.data.engineGearboxElectrics?.fuelType === 'Napęd elektryczny'
								? 'efficiencyDetails.energyConsumption'
								: 'efficiencyDetails.fuelConsumption',
						)}`}
					>
						{technicalData.state === DataRequestState.Requested && !isUserAuthorized ? (
							<Skeleton height={32} />
						) : (
							<Text className="text-with-ref" appearance={TokenTextAppearance.bigcopy100}>
								{fuelConsumption}{' '}
								<button type="button" className="disclaimer-button" onClick={() => onClickDisclaimer(0)}>
									{footnotes[0].reference}
								</button>
							</Text>
						)}
					</Fieldset>

					<Divider />
				</div>

				<div className="remove-span-child">
					<Fieldset
						legend={`${t(
							// TODO 15082022 do in backend
							technicalData.data.engineGearboxElectrics?.fuelType === 'Elektro' ||
								technicalData.data.engineGearboxElectrics?.fuelType === 'Electric' ||
								technicalData.data.engineGearboxElectrics?.fuelType === 'Napęd elektryczny'
								? 'efficiencyDetails.electricalRange'
								: 'efficiencyDetails.emissionCategory',
						)}`}
					>
						{technicalData.state === DataRequestState.Requested && !isUserAuthorized ? (
							<Skeleton height={32} />
						) : (
							<Text appearance={TokenTextAppearance.bigcopy100}>
								{emission}{' '}
								<button type="button" className="disclaimer-button" onClick={() => onClickDisclaimer(0)}>
									{footnotes[0].reference}
								</button>
							</Text>
						)}
					</Fieldset>

					<Divider />
				</div>
			</Container>

			<Container wrap="always" gutter={ContainerGutter.dynamic0130} padding={{ top: ContainerPadding.dynamic0130 }}>
				<>
					<RecallButton numberRecalls={numberRecalls} recallRequestStatus={recallRequestStatus} toggleRecallsLayer={toggleRecallsLayer} />
				</>

				{/* TODO 25012022 Remove disabled property as soon as the layer update bug is fixed */}
				<CTA
					tag="button"
					emphasis="secondary"
					onClick={() => {
						vinCheckTrackingButton();
						toggleTechnicalDetailsLayer();
					}}
					disabled={technicalData.state !== DataRequestState.Available}
					stretchContent
				>
					{t('efficiencyDetails.moreInformation')}
				</CTA>
			</Container>
		</>
	);
}

interface Props {
	vehicle: VehicleObject;
	technicalData: TechnicalDataObject;
	numberRecalls: number;
	isUserAuthorized: boolean;
	recallRequestStatus: DataRequestState;
	getConsumption: () => string;
	getEmissionOrRange: () => string;
	toggleRecallsLayer: () => void;
	toggleTechnicalDetailsLayer: () => void;
	trackingService: any;
	isLoggedIn: boolean;
	onClickDisclaimer: (disclaimerNumber: number) => void;
	footnotes: { reference: string; text: string; handle: LayerHandleV2<any> }[];
}
